import { CheckoutPathType, PageType } from 'AppShell/appshell_types';
import useRouter from 'AppShell/hooks/useRouter';
import { convertUrlToListingPageState } from 'Client/components/ListingPage/services/listingPageUrlService';
import { getPageTypeByUrl } from 'Client/hooks/UrlManager/pageTypeService';
import { useBellaSelector } from 'Client/redux/hooks';
import { ListingPageUrlState } from 'Client/redux/products/types';
import { ensureEndingSlash, trimLeadingSlash, trimTrailingSlash } from 'Client/urlService';
import { capitalize } from 'Common/formatting/formattingService';
import { Pathname } from 'history';
import { PageNameMapping } from './SegmentTracker';
import routes from 'Common/routes/paths';

const SegmentPageTypeMapping = {
    [PageType.CATEGORY]: 'Category',
    [PageType.ALLPRODUCTS]: 'Search',
    [PageType.BRANDS]: 'Brand',
    [PageType.NEWPRODUCTS]: 'New Products',
    [PageType.LANDING]: 'Landing Page',
    [PageType.LOOKS]: 'Looks Detail',
    [PageType.LOOKS_WISHLIST]: 'Looks Wishlist',
};

const checkoutPageLabel = {
    [CheckoutPathType.REDIRECT]: 'Redirect',
    [CheckoutPathType.APP_REDIRECT]: 'Redirect',
    [CheckoutPathType.LOGIN]: 'Login/Registration',
    [CheckoutPathType.APP_LOGIN]: 'Login/Registration',
    [CheckoutPathType.ADDRESS]: 'Address',
    [CheckoutPathType.APP_ADDRESS]: 'Address',
    [CheckoutPathType.ORDER_CONFIRMATION]: 'Order Overview',
    [CheckoutPathType.APP_ORDER_CONFIRMATION]: 'Order Overview',
    [CheckoutPathType.ORDER_SUCCESS]: 'Order Success',
    [CheckoutPathType.APP_ORDER_SUCCESS]: 'Order Success',
};

const getPageLabel = ({
    bellaPageType,
    urlState,
    totalCount,
    lookDetailPageSlug,
    pathname,
}: {
    bellaPageType: PageType;
    urlState: ListingPageUrlState;
    totalCount: number;
    lookDetailPageSlug?: string;
    pathname: Pathname;
}) => {
    const listingPages = [PageType.CATEGORY, PageType.BRANDS, PageType.NEWPRODUCTS, PageType.LANDING, PageType.LOOKS];
    const checkoutPages = [PageType.CHECKOUT, PageType.CHECKOUT_NATIVE];

    if (lookDetailPageSlug) {
        return lookDetailPageSlug;
    }

    if (listingPages.includes(bellaPageType)) {
        return capitalize(trimLeadingSlash(urlState.listingPageKey));
    }

    if (bellaPageType === PageType.ALLPRODUCTS) {
        return !totalCount ? 'Empty search result' : '';
    }

    if (checkoutPages.includes(bellaPageType) && checkoutPageLabel[ensureEndingSlash(pathname)]) {
        return checkoutPageLabel[ensureEndingSlash(pathname)];
    }

    return '';
};

const getPageType = ({
    pageType,
    lookDetailPageSlug,
    search,
}: {
    pageType: PageType;
    lookDetailPageSlug?: string;
    search: string;
}): keyof typeof SegmentPageTypeMapping | '' => {
    // if LLP then empty page type
    if (pageType === PageType.LOOKS && !lookDetailPageSlug) {
        return '';
    }

    if (pageType === PageType.ALLPRODUCTS && !search) {
        return '';
    }

    return SegmentPageTypeMapping[pageType];
};

const getPageName = ({
    pathname,
    pageType,
    lookDetailPageSlug,
}: {
    pathname: string;
    pageType: PageType;
    lookDetailPageSlug?: string;
}) => {
    if (pathname.includes('/brands/')) {
        return 'Brand Listing';
    }

    if (lookDetailPageSlug) {
        return 'Looks Detail';
    }

    if (pageType === PageType.INTERIOR_DESIGN_SERVICE_FORM) {
        return 'Westwing Studio Form';
    }

    return PageNameMapping[pageType];
};

const getPageCategory = ({ pageType, urlState }: { pageType: PageType; urlState: ListingPageUrlState }) =>
    pageType === PageType.CATEGORY ? urlState.listingPageKey : '';

const getLookDetailPageSlug = (pathname: string) => {
    const pathWithoutPrefix = pathname.replace(trimTrailingSlash(routes.LOOKS), '');
    const lookSlug = trimLeadingSlash(trimTrailingSlash(pathWithoutPrefix));

    return lookSlug;
};

export const useSegmentTrackingInfo = () => {
    const {
        location: { pathname, search },
        host,
    } = useRouter();
    const totalCount = useBellaSelector(state => state.products.totalCount);

    const urlState = convertUrlToListingPageState(pathname, search);
    const { pageType: bellaPageType } = getPageTypeByUrl(pathname, host);

    const pageCategory = getPageCategory({ pageType: bellaPageType, urlState });

    const lookDetailPageSlug = bellaPageType === PageType.LOOKS ? getLookDetailPageSlug(pathname) : undefined;
    const pageName = getPageName({ pathname, pageType: bellaPageType, lookDetailPageSlug });
    const pageType = getPageType({
        pageType: bellaPageType,
        lookDetailPageSlug,
        search,
    });
    const pageLabel = getPageLabel({ bellaPageType, urlState, totalCount, lookDetailPageSlug, pathname });

    return { pageCategory, pageName, pageType, pageLabel, pathname };
};
